import React, { useMemo, useState } from "react";
import { Modal, Button, Input, Radio, Group } from "@mantine/core";
import B2BButton from "../../common/B2BButton";
import { createB2BAPI } from "../../../api/Interceptor";
import { useLocation, useNavigate } from "react-router-dom";

const IdentityVerificationModal = ({
    opened,
    setModalOpen,
    handlePlaceOrder,
}) => {
    const [verificationType, setVerificationType] = useState("aadhaar");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const B2B_API = useMemo(() => createB2BAPI(navigate), [navigate]);
    const [aadhaarNumber, setAadhaarNumber] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const [otp, setOtp] = useState("");
    const [aadhaarStep, setAadhaarStep] = useState(1);
    const [isAadhaarVerified, setIsAadhaarVerified] = useState(false);
    const[aadharLoading,setAadhaarLoading]=useState(false);
    const [pan, setPan] = useState("");
    const [name, setName] = useState("");
    const [dob, setDob] = useState("");
    const[aadharError,setAadhaarError]=useState("");
    const [isPanVerified, setIsPanVerified] = useState(false);


    const resetFields = () => {
        setVerificationType("aadhaar");
        setAadhaarNumber("");
        setMobileNumber("");
        setPan("");
        setDob("");
        setName("");
    };

    const handleClose = () => {
        setModalOpen(false);
        resetFields();
    };

    const handleSendOtp = async () => {
        setAadhaarLoading(true);
        setAadhaarError("");

        try {
            const cleanAadhaar = aadhaarNumber.replace(/\s/g, "");
            const response = await B2B_API.post("aadhaar/send-otp", {
                json: {
                    aadhaarNumber: cleanAadhaar,
                    mobileNumber,
                    consent: "Y",
                },
            }).json();

            if (response?.data?.success || response?.message?.includes("OTP sent")) {
                setTransactionId(response?.data?.txnId || response?.data?.transactionId);
                setAadhaarStep(2);
                setAadhaarError("");
            } else {
                setAadhaarError(response?.message || "Failed to send OTP");
            }
        } catch (error) {
            setAadhaarError("Network or server error. Please try again.");
        } finally {
            setAadhaarLoading(false);
        }
    };



    const handleVerifyOtp = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await B2B_API.post("aadhaar/verify", {
                json: {
                    aadhaarNumber,
                    otp,
                    transactionId: referenceId,
                },
            }).json();

            if (response?.status === "SUCCESS") {
                setIsAadhaarVerified(true);
                setTimeout(() => {
                    onClose();
                    handlePlaceOrder();
                }, 2000);
            } else {
                setError(response?.message || "OTP verification failed.");
            }
        } catch (err) {
            setError("Verification failed. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    const handleVerifyPan = async () => {
        setLoading(true);
        setError("");
        try {
            const response = await B2B_API.post("aadhaar/verify-pan", {
                json: {
                    pan,
                    name,
                    dob,
                },
            }).json();

            if (response?.status === "SUCCESS") {
                setIsPanVerified(true);
                setTimeout(() => {
                    onClose();
                    handlePlaceOrder();
                }, 2000);
            } else {
                setError(response?.message || "PAN verification failed.");
            }
        } catch (err) {
            setError("Error verifying PAN. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    return (
        <Modal
            opened={opened}
            onClose={handleClose}
            title="Identity Verification"
            centered
            overlayProps={{ opacity: 0.55, blur: 3 }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "center",
                    marginBottom: "16px",
                }}
            >
                <Radio.Group value={verificationType}>
                    <Group gap="xs" mt="xs">
                        <Radio
                            value="aadhaar"
                            label="Aadhaar"
                            size="sm"
                            styles={{ label: { fontSize: 14 } }}
                            onClick={() => setVerificationType("aadhaar")}
                        />
                        <Radio
                            value="pan"
                            label="PAN Card"
                            size="sm"
                            styles={{ label: { fontSize: 14 } }}
                            onClick={() => setVerificationType("pan")}
                        />
                    </Group>
                </Radio.Group>
            </div>

            {verificationType === "aadhaar" && (
                <>
                    {aadhaarStep === 1 && !isAadhaarVerified && (
                        <>
                            <Input
                                label="Aadhaar Number"
                                placeholder="Enter 12-digit Aadhaar number"
                                value={aadhaarNumber}
                                onChange={(e) =>
                                    setAadhaarNumber(e.target.value.replace(/\D/g, "").slice(0, 12))
                                }
                                maxLength={12}
                                styles={{ input: { textAlign: "center", letterSpacing: "2px" } }}
                            />
                            <Input
                                label="Registered Mobile Number"
                                placeholder="Enter 10-digit mobile number"
                                style={{ marginTop: "12px" }}
                                value={mobileNumber}
                                onChange={(e) =>
                                    setMobileNumber(e.target.value.replace(/\D/g, "").slice(0, 10))
                                }
                                maxLength={10}
                            />
                            {error && (
                                <div style={{ color: "red", marginTop: "8px", textAlign: "center" }}>
                                    {error}
                                </div>
                            )}
                            <B2BButton
                                name={loading ? "Sending OTP..." : "Send OTP"}
                                onClick={handleSendOtp}
                                disabled={loading || aadhaarNumber.length !== 12 || mobileNumber.length !== 10}
                                color="#8BC54F"
                                style={{ width: "100%", marginTop: "16px" }}
                            />
                        </>
                    )}

                    {aadhaarStep === 2 && !isAadhaarVerified && (
                        <>
                            <Input
                                label="Enter OTP"
                                placeholder="6-digit OTP"
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                maxLength={6}
                                styles={{ input: { textAlign: "center", letterSpacing: "4px" } }}
                            />
                            {error && (
                                <div style={{ color: "red", marginTop: "8px", textAlign: "center" }}>
                                    {error}
                                </div>
                            )}
                            <B2BButton
                                name={loading ? "Verifying..." : "Verify OTP"}
                                onClick={handleVerifyOtp}
                                disabled={loading || otp.length !== 6}
                                color="#8BC54F"
                                style={{ width: "100%", marginTop: "16px" }}
                            />
                        </>
                    )}

                    {isAadhaarVerified && (
                        <div style={{ textAlign: "center", padding: "20px 0" }}>
                            <div style={{ fontSize: "48px", color: "#8BC54F", marginBottom: "8px" }}>
                                ✅
                            </div>
                            <h3 style={{ color: "#8BC54F" }}>Aadhaar Verified Successfully!</h3>
                            <p style={{ color: "gray" }}>Your order is being processed...</p>
                        </div>
                    )}
                </>
            )}

            {verificationType === "pan" && (
                <>
                    {!isPanVerified ? (
                        <>
                            <Input
                                label="PAN Number"
                                placeholder="ABCDE1234F"
                                value={pan}
                                onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
                                maxLength={10}
                                styles={{ input: { textAlign: "center", letterSpacing: "2px" } }}
                            />
                            <Input
                                label="Full Name (Optional)"
                                placeholder="Enter full name"
                                style={{ marginTop: "12px" }}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            <Input
                                label="Date of Birth (Optional)"
                                type="date"
                                style={{ marginTop: "12px" }}
                                value={dob}
                                onChange={(e) => setDob(e.target.value)}
                            />
                            {error && (
                                <div style={{ color: "red", marginTop: "8px", textAlign: "center" }}>
                                    {error}
                                </div>
                            )}
                            <B2BButton
                                name={loading ? "Verifying..." : "Verify PAN"}
                                onClick={handleVerifyPan}
                                disabled={loading || pan.length !== 10}
                                color="#8BC54F"
                                style={{ width: "100%", marginTop: "16px" }}
                            />
                        </>
                    ) : (
                        <div style={{ textAlign: "center", padding: "20px 0" }}>
                            <div style={{ fontSize: "48px", color: "#8BC54F", marginBottom: "8px" }}>
                            </div>
                            <h3 style={{ color: "#8BC54F" }}>PAN Verified Successfully!</h3>
                            <p style={{ color: "gray" }}>Your order is being processed...</p>
                        </div>
                    )}
                </>
            )}
        </Modal>
    );
};

export default IdentityVerificationModal;
